import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, HostListener, Input } from '@angular/core';
import { CapacityByPersonService } from './capacity-by-person.service';
import { 
  CapacityByPersonData, 
  FilterOptions, 
  GaugeChartData, 
  GaugeChartSeries,
  GaugeDisplayData,
  CapacityByPersonResponse
} from '../../models/capacity-by-person.models';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-capacity-by-person',
    templateUrl: './capacity-by-person.component.html',
    styleUrls: ['./capacity-by-person.component.scss'],
    standalone: false
})
export class CapacityByPersonComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  // Input property to control filter visibility from parent component
  @Input() isFilterVisible: boolean = false;

  // Chart options
  view: any[] = [500, 300];
  legend: boolean = true;
  legendPosition: string = 'bottom';
  legendTitle: string = 'Legend';
  
  // Chart data
  gaugeData: GaugeChartSeries[] = [];
  displayData: GaugeDisplayData | null = null;
  remainingTimeText: string = '';
  
  // Color scheme
  colorScheme = {
    domain: ['#5AA454', '#E44D25', '#42A5F5'] // Green for available, Red for assigned, Blue for remaining
  };
  
  // Gauge options
  gaugeMin: number = 0;
  gaugeMax: number = 0; // Will be set dynamically based on available time
  gaugeUnits: string = 'hrs';
  gaugeAngleSpan: number = 180;
  gaugeStartAngle: number = -90;
  gaugeShowAxis: boolean = true;
  gaugeValue: number = 0;
  gaugePreviousValue: number = 0;
  gaugeShowText: boolean = true;
  showLegend: boolean = true;
  
  // Date range filter
  dateRangeValue: Date[] = [];
  minDateValue: Date = new Date(2020, 0, 1);
  maxDateValue: Date = new Date(2050, 11, 31); // Allow dates far into the future
  
  // Filters
  filters: FilterOptions = {
    startDate: '',
    endDate: '',
    tkId: null,
    practiceAreaId: null
  };
  
  // Filter options
  tkOptions: { id: string; name: string }[] = []; // Will be populated from API
  practiceAreaOptions: { id: string; name: string }[] = []; // Will be populated from API
  
  // Filter visibility - always true as it's controlled by parent through isFilterVisible
  showFilters: boolean = true;
  
  // UI state
  loading: boolean = false;
  error: string | null = null;
  noData: boolean = false;
  
  constructor(private capacityService: CapacityByPersonService) { }

  ngOnInit(): void {
    // Initialize date range with default values (today to today + 30 days)
    const today = new Date();
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 30);
    
    // Set default date range for p-calendar
    this.dateRangeValue = [today, defaultEndDate];
    
    // Set initial filter values with formatted dates
    this.filters.startDate = this.formatDate(today);
    this.filters.endDate = this.formatDate(defaultEndDate);
    
    // Fetch filter options
    this.fetchFilterOptions();
    
    // Load initial data with only date filters
    this.loadInitialData();
  }
  
  /**
   * Fetch filter options from API
   */
  fetchFilterOptions(): void {
    // Fetch TK users
    this.capacityService.getTKUsers()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.tkOptions = response.data;
            console.log('TK users loaded:', this.tkOptions);
            
            // If we already have capacity data with a userName, try to match it now
            if (this.displayData && this.displayData.userName) {
              const userName = this.displayData.userName; // Store in local variable to avoid null reference
              const matchingTkOption = this.tkOptions.find(option => 
                option.name === userName
              );
              
              if (matchingTkOption) {
                // Set the selected TK in the filters
                this.filters.tkId = matchingTkOption.id;
                console.log('Auto-selected TK user after options loaded:', matchingTkOption.name);
              }
            }
          }
        },
        error: (error) => {
          console.error('Error loading TK users:', error);
        }
      });
      
    // Fetch practice areas
    this.capacityService.getPracticeAreas()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.practiceAreaOptions = response.data;
            console.log('Practice areas loaded:', this.practiceAreaOptions);
          } else {
            console.warn('No practice area data received');
          }
        },
        error: (error) => {
          console.error('Error loading practice areas:', error);
        }
      });
  }
  
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.updateChartSize();
    }, 0);
  }
  
  /**
   * Load initial data with only date filters and select first TK user
   */
  loadInitialData(): void {
    this.loading = true;
    this.error = null;
    this.noData = false;
    
    // Create a copy of filters with only date parameters
    const initialFilters = {
      startDate: this.filters.startDate,
      endDate: this.filters.endDate,
      tkId: null,
      practiceAreaId: null
    };
    
    this.capacityService.getCapacityByPersonData(initialFilters)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            // Process the data for display
            this.processData(response.data);
            
            // Get the first user from the response
            const firstUser = response.data[0];
            
            // Find the corresponding TK option by userName
            if (firstUser.userName) {
              const userName = firstUser.userName; // Store in local variable to avoid null reference
              // Check if TK options are already loaded
              if (this.tkOptions && this.tkOptions.length > 0) {
                // Find the TK option that matches the userName from the response
                const matchingTkOption = this.tkOptions.find(option => 
                  option.name === userName
                );
                
                if (matchingTkOption) {
                  // Set the selected TK in the filters
                  this.filters.tkId = matchingTkOption.id;
                  console.log('Auto-selected TK user immediately:', matchingTkOption.name);
                }
              }
              // Note: If TK options aren't loaded yet, the fetchFilterOptions method will handle the selection
              // when the options do load, by checking this.displayData.userName
            }
          } else {
            this.noData = true;
          }
        },
        error: (err) => {
          console.error('API error:', err);
          this.error = err.message || 'Failed to load capacity data';
        }
      });
  }
  
  /**
   * Load capacity data from service
   */
  loadData(): void {
    this.loading = true;
    this.error = null;
    this.noData = false;
    
    this.capacityService.getCapacityByPersonData(this.filters)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.processData(response.data);
          } else {
            this.noData = true;
          }
        },
        error: (err) => {
          console.error('API error:', err);
          this.error = err.message || 'Failed to load capacity data';
        }
      });
  }
  
  /**
   * Process capacity data for display
   */
  processData(data: CapacityByPersonData[]): void {
    if (!data || data.length === 0) {
      this.noData = true;
      return;
    }
    
    // Use the first person's data
    const personData = data[0];
    
    // Convert seconds to hours for better readability and round to whole numbers
    const availableTime = Math.round(this.secondsToHours(personData.availableTime || 0));
    const assignedTime = Math.round(this.secondsToHours(personData.assignedTime || 0));
    const remainingTime = Math.max(0, availableTime - assignedTime); // Ensure not negative
    
    // Set gauge max based on available time
    this.gaugeMax = Math.max(availableTime, 100); // Ensure at least 100 for visibility
    
    // Create display data
    this.displayData = {
      userName: personData.userName || 'Unknown',
      availableTime,
      assignedTime,
      remainingTime,
      percentAssigned: availableTime > 0 ? (assignedTime / availableTime) * 100 : 0
    };
    
    // Format for gauge chart
    this.gaugeData = [{
      name: 'Capacity',
      series: [
        { name: 'Available', value: availableTime },
        { name: 'Assigned', value: assignedTime },
        { name: 'Remaining', value: remainingTime }
      ]
    }];
    
    // Set text value for gauge - using whole numbers
    this.remainingTimeText = `${remainingTime} remaining`;
    
    // Try to update the TK dropdown selection if options are loaded
    this.updateTkSelection(personData.userName);
    
    console.log('Gauge data processed:', this.gaugeData);
  }
  
  /**
   * Update the TK dropdown selection based on userName
   */
  private updateTkSelection(userName: string | undefined): void {
    if (!userName || !this.tkOptions || this.tkOptions.length === 0) {
      return;
    }
    
    const matchingTkOption = this.tkOptions.find(option => option.name === userName);
    
    if (matchingTkOption) {
      // Set the selected TK in the filters
      this.filters.tkId = matchingTkOption.id;
      console.log('Updated TK selection to:', matchingTkOption.name);
    }
  }
  
  /**
   * Convert seconds to hours and round to whole numbers
   */
  secondsToHours(seconds: number): number {
    return Math.round(seconds / 3600);
  }
  
  /**
   * Format date to MM/DD/YY
   */
  formatDate(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear().toString().substr(2); // Get last 2 digits of year
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${month}/${day}/${year}`;
  }
  
  /**
   * Apply filters and reload data
   */
  applyFilters(): void {
    // Update date range filters
    if (this.dateRangeValue && this.dateRangeValue.length === 2) {
      this.filters.startDate = this.formatDate(this.dateRangeValue[0]);
      this.filters.endDate = this.formatDate(this.dateRangeValue[1]);
    }
    
    this.loadData();
  }
  
  /**
   * Reset filters to default values
   */
  resetFilters(): void {
    // Reset to default date range (today to today + 30 days)
    const today = new Date();
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 30);
    
    // Update the calendar component
    this.dateRangeValue = [today, defaultEndDate];
    
    this.filters = {
      startDate: this.formatDate(today),
      endDate: this.formatDate(defaultEndDate),
      tkId: null,
      practiceAreaId: null
    };
    
    this.loadData();
  }
  
  /**
   * Toggle filter visibility
   * Note: This method is now controlled by the parent component through isFilterVisible
   */
  toggleFilters(): void {
    // This method is now deprecated as filter visibility is controlled by the parent
    // through the isFilterVisible input property
    this.showFilters = true; // Always true as visibility is controlled by isFilterVisible
  }
  
  /**
   * Update chart size on window resize
   */
  @HostListener('window:resize')
  onResize(): void {
    this.updateChartSize();
  }
  
  /**
   * Update chart size based on container width - gauge charts use aspect ratio
   */
  private updateChartSize(): void {
    if (this.chartContainer) {
      const width = this.chartContainer.nativeElement.offsetWidth;
      // Gauge charts work well with 0.6 aspect ratio
      const height = Math.min(width * 0.6, 350); // Compact height for gauge
      this.view = [width, height];
    }
  }
}
